<script setup>
/**
 * The buttons under a piece of wording, edited in place.
 *
 * FOUR TYPES, AND THE CHOICE IS THE PRODUCT DECISION. A quick reply, a link, a call button and a
 * copy-code button all preview through `CallToActionTemplate` with no new renderer, and all four
 * can actually be SENT by Chatwoot's own send path. Meta's vocabulary is far larger — OTP, FLOW,
 * MPM, SPM, CATALOG, VOICE_CALL, ORDER_DETAILS, plus several that exist only in its webhook enum —
 * and every one of them is READ and named on the row's facts panel. None is offered here, because
 * each needs something an SMB does not have (a published Flow, a product catalogue, an Android
 * app signing hash) or a send path this product does not have. VOICE_CALL additionally needs
 * WhatsApp Calling, which is Chatwoot Enterprise and off-limits to Waslo by owner rule.
 *
 * Offering one would cost the owner a template slot and a review cycle to find out at send time
 * that it cannot be sent.
 *
 * ── THREE RULES HERE THAT ARE INVISIBLE IN A FORM ────────────────────────────────────
 *
 * 1. THE CAPS ARE PER TYPE (10 quick replies, 2 links, 1 call button, 1 copy code), which is why
 *    "Add" is disabled per type rather than once for the whole list: a single "you have too many
 *    buttons" on a form with four kinds of button names the wrong problem.
 * 2. THE ORDER MATTERS. Meta requires quick replies to form one contiguous group, so
 *    `[reply, link, reply]` is refused while `[reply, reply, link]` is fine — a rule no per-button
 *    check can see, and one Graph reports as "invalid combination". The list says so where the
 *    order is, and offers the fix rather than only the diagnosis.
 * 3. A COPY-CODE BUTTON HAS NO LABEL. "Copy code button text cannot be customized" — WhatsApp
 *    draws it, translated, itself. So that row asks for the CODE and hides the label field, and
 *    the preview borrows a translated label that is never sent.
 *
 * A BUTTON THIS EDITOR CANNOT WRITE NEVER REACHES THIS LIST. It used to, shown read-only and
 * described as surviving the round trip untouched — which was false: `buttonToWire` never read
 * the stored original, so a FLOW button went back to Meta with its `flow_id` gone and the save was
 * refused. The refusal now happens one screen earlier, in `editBlockers`, which will not open the
 * editor for such a template and says which button and where to edit it instead.
 *
 * `v-for` keys off `button.key`, a local counter that is never sent — removing the first of
 * three buttons otherwise re-uses the removed row's DOM for its successor and carries a focus
 * ring and a half-typed value with it.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import {
  BUTTON_CAPS,
  BUTTON_TOTAL_CAP,
  BUTTON_TYPES,
  BUTTONS_SHOWN_WITHOUT_TAP,
  LIMITS,
  buttonRow,
  buttonsGrouped,
} from '../helpers/template-draft.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** `[{ key, type, text, url, urlSample, phoneNumber, code }]`. */
  buttons: { type: Array, default: () => [] },
  /** One sentence about the list as a whole, from `validateDraft`. */
  message: { type: String, default: '' },
  /**
   * One sentence PER BUTTON, aligned to `buttons` by index, from `buttonProblems`.
   *
   * The list-level `message` above names the first broken row by number and sits at the bottom
   * of the section — the right shape for a refusal read after a Save, and useless while four
   * rows are being edited. These sit on the row they are about and arrive as she types.
   */
  problems: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:buttons']);

const { t } = useWasloI18n();

const typeOptions = computed(() =>
  BUTTON_TYPES.map(type => ({
    value: type,
    label: t(`WASLO.TEMPLATES.EDITOR.BUTTONS.TYPES.${type}`),
  }))
);

const countOf = type =>
  props.buttons.filter(button => button.type === type).length;

/** Which types still have room, per type AND against Meta's total of ten. */
const addable = computed(() => {
  if (props.buttons.length >= BUTTON_TOTAL_CAP) return [];
  return BUTTON_TYPES.filter(type => countOf(type) < BUTTON_CAPS[type]);
});

/** Meta's grouping rule, checked live so the fix is offered before the submission. */
const grouped = computed(() => buttonsGrouped(props.buttons));

const problemAt = index => props.problems?.[index] ?? '';

/**
 * Above three buttons WhatsApp delivers two and hides the rest behind "See all options"; a
 * template with four or more, or one mixing quick replies with other types, cannot be opened on
 * WhatsApp Desktop at all. Both are Meta's own words, and both are things an owner would rather
 * know now than after a customer asks why she cannot see the button.
 */
const crowded = computed(
  () => props.buttons.length > BUTTONS_SHOWN_WITHOUT_TAP
);

const replace = (index, patch) => {
  emit(
    'update:buttons',
    props.buttons.map((button, position) =>
      position === index ? { ...button, ...patch } : button
    )
  );
};

const add = type => {
  emit('update:buttons', [...props.buttons, buttonRow({ type: type })]);
};

const remove = index => {
  emit(
    'update:buttons',
    props.buttons.filter((_button, position) => position !== index)
  );
};

/** Moves every quick reply to the front, which is the shortest route to Meta's two groups. */
const regroup = () => {
  const quick = props.buttons.filter(button => button.type === 'QUICK_REPLY');
  const rest = props.buttons.filter(button => button.type !== 'QUICK_REPLY');
  emit('update:buttons', [...quick, ...rest]);
};
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-template-buttons>
    <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
      {{ t('WASLO.TEMPLATES.EDITOR.BUTTONS.DESCRIPTION') }}
    </p>

    <ul v-if="buttons.length" class="flex flex-col gap-3 mb-0 list-none">
      <li
        v-for="(button, index) in buttons"
        :key="button.key"
        class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
        :data-waslo-template-button="index"
      >
        <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
          <WasloSelect
            class="sm:w-56"
            :model-value="button.type"
            :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.TYPE_LABEL')"
            :options="typeOptions"
            :disabled="disabled"
            @update:model-value="value => replace(index, { type: value })"
          />
          <!-- No label field for a copy-code button: WhatsApp writes that one itself. -->
          <!--
            `TEXT_HINT` existed in both language tables and was bound to nothing, so the one
            limit on this screen that was never disclosed was the one an owner meets first.
          -->
          <WasloInput
            v-if="button.type !== 'COPY_CODE'"
            class="flex-1 min-w-0"
            :model-value="button.text"
            :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.TEXT_LABEL')"
            :hint="t('WASLO.TEMPLATES.EDITOR.BUTTONS.TEXT_HINT')"
            :placeholder="t('WASLO.TEMPLATES.EDITOR.BUTTONS.TEXT_PLACEHOLDER')"
            :max-length="LIMITS.BUTTON_TEXT"
            :disabled="disabled"
            :data-waslo-template-button-text="index"
            @update:model-value="value => replace(index, { text: value })"
          />
        </div>

        <WasloInput
          v-if="button.type === 'URL'"
          :model-value="button.url"
          :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.URL_LABEL')"
          :hint="t('WASLO.TEMPLATES.EDITOR.BUTTONS.URL_HINT')"
          :max-length="LIMITS.URL"
          :disabled="disabled"
          :data-waslo-template-button-url="index"
          @update:model-value="value => replace(index, { url: value })"
        />

        <!--
          Only when the address actually has a blank in it. Meta needs ONE example value for it
          and refuses the template without one; asking for it unconditionally would be a field
          most owners never need.
        -->
        <WasloInput
          v-if="button.type === 'URL' && button.url.includes('{{')"
          :model-value="button.urlSample"
          :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.URL_SAMPLE_LABEL')"
          :hint="t('WASLO.TEMPLATES.EDITOR.BUTTONS.URL_SAMPLE_HINT')"
          :disabled="disabled"
          :data-waslo-template-button-url-sample="index"
          @update:model-value="value => replace(index, { urlSample: value })"
        />

        <WasloInput
          v-if="button.type === 'PHONE_NUMBER'"
          :model-value="button.phoneNumber"
          :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.PHONE_LABEL')"
          :hint="t('WASLO.TEMPLATES.EDITOR.BUTTONS.PHONE_HINT')"
          :max-length="LIMITS.PHONE_NUMBER"
          :disabled="disabled"
          @update:model-value="value => replace(index, { phoneNumber: value })"
        />

        <WasloInput
          v-if="button.type === 'COPY_CODE'"
          :model-value="button.code"
          :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.CODE_LABEL')"
          :hint="t('WASLO.TEMPLATES.EDITOR.BUTTONS.CODE_HINT')"
          :max-length="LIMITS.COPY_CODE"
          :disabled="disabled"
          :data-waslo-template-button-code="index"
          @update:model-value="value => replace(index, { code: value })"
        />

        <!-- The row's own sentence, on the row, while she is still in it. -->
        <p
          v-if="problemAt(index)"
          class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
          :data-waslo-template-button-problem="index"
        >
          {{ problemAt(index) }}
        </p>

        <div class="flex">
          <Button
            :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.REMOVE')"
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            :disabled="disabled"
            :data-waslo-template-button-remove="index"
            @click="remove(index)"
          />
        </div>
      </li>
    </ul>

    <!--
      THE GROUPING RULE, WITH ITS FIX ATTACHED. Every button is individually valid and within every
      cap; only the ORDER is wrong, and Graph's own refusal says "invalid combination" and names
      nothing. One press puts the quick replies together.
    -->
    <div
      v-if="!grouped"
      class="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-n-amber-3"
      data-waslo-template-buttons-grouping
    >
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11">
        {{ t('WASLO.TEMPLATES.EDITOR.BUTTONS.GROUPING') }}
      </p>
      <Button
        :label="t('WASLO.TEMPLATES.EDITOR.BUTTONS.REGROUP')"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="disabled"
        data-waslo-template-buttons-regroup
        @click="regroup"
      />
    </div>

    <!--
      HOW WHATSAPP WILL DRAW WHAT SHE HAS, printed live under the list — the same statement the
      menu editor makes about three-options-are-buttons and four-are-a-list. It is not a choice
      and not a warning; it is a consequence of the count, and the composer says it so that "why
      can my customer only see two buttons" is answered before it is asked.
    -->
    <p
      v-if="buttons.length && !crowded"
      class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
      data-waslo-template-buttons-shape
    >
      {{ t('WASLO.TEMPLATES.EDITOR.BUTTONS.SHAPE', { count: buttons.length }) }}
    </p>

    <p
      v-if="crowded"
      class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
      data-waslo-template-buttons-crowded
    >
      {{
        t('WASLO.TEMPLATES.EDITOR.BUTTONS.CROWDED', {
          count: buttons.length,
          shown: BUTTONS_SHOWN_WITHOUT_TAP - 1,
          hidden: buttons.length - (BUTTONS_SHOWN_WITHOUT_TAP - 1),
        })
      }}
    </p>

    <div class="flex flex-wrap gap-2">
      <Button
        v-for="type in addable"
        :key="type"
        :label="
          t('WASLO.TEMPLATES.EDITOR.BUTTONS.ADD', {
            type: t(`WASLO.TEMPLATES.EDITOR.BUTTONS.TYPES.${type}`),
          })
        "
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="disabled"
        :data-waslo-template-button-add="type"
        @click="add(type)"
      />
      <!--
        Not an error and not hidden: every cap reached is a normal, finished state, and a row of
        buttons that simply vanishes leaves an owner wondering what she did wrong.
      -->
      <p
        v-if="addable.length === 0"
        class="mb-0 text-xs leading-5 text-n-slate-10"
        data-waslo-template-buttons-full
      >
        {{ t('WASLO.TEMPLATES.EDITOR.BUTTONS.FULL') }}
      </p>
    </div>

    <p
      v-if="message"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
      data-waslo-template-buttons-error
    >
      {{ message }}
    </p>
  </div>
</template>
